import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import FormField from '../FormField';

const ObjectField = ({
  name,
  schema,
  value,
  onChange,
  onBlur,
  error,
  touched,
  customComponents,
}) => {
  const handleFieldChange = useCallback(
    (fieldName, fieldValue) => {
      onChange(name, { ...value, [fieldName]: fieldValue });
    },
    [name, value, onChange]
  );

  const handleFieldBlur = useCallback(
    (fieldName) => {
      onBlur(`${name}.${fieldName}`);
    },
    [name, onBlur]
  );

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        background: 'transparent',
        '& .MuiAccordionDetails-root': {
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--canvas-form-gap, 22px)',
          pt: 0.5,
          pb: 1.5,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${name}-content`}
        id={`${name}-header`}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {schema.title || name}
          {schema.description && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 400 }}>{schema.description}</Typography>}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id={`${name}-content`}>
        <Box
          sx={{
            pl: 1,
            pt: 'var(--canvas-form-label-gutter, 12px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--canvas-form-gap, 22px)',
          }}
        >
          {Object.entries(schema.properties || {}).map(([fieldName, fieldSchema]) => (
            <FormField
              key={fieldName}
              name={`${name}.${fieldName}`}
              schema={fieldSchema}
              value={(value || {})[fieldName]}
              onChange={(_key, fieldValue) =>
                handleFieldChange(fieldName, fieldValue)
              }
              onBlur={() => handleFieldBlur(fieldName)}
              error={error && error[fieldName]}
              touched={touched && touched[fieldName]}
              required={(schema.required || []).includes(fieldName)}
              customComponents={customComponents}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

ObjectField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.object,
  touched: PropTypes.object,
  customComponents: PropTypes.object,
};

export default React.memo(ObjectField);
